import React, { FC, useEffect, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';

import Navbar from '../../components/Navbar';
import HeaderHome from '../../components/HeaderHome';
import WhyHome from '../../components/WhyHome';
import GuideHome from '../../components/GuideHome';
import ReadytoGetStarted from '../../components/ReadytoGetStarted';
import BitcoinPriceSection from '../../components/BitcoinPriceSection';
import Footer from '../../components/Footer';
import Faq from '../../components/Faq';
import InterlinkSection from '../../components/InterlinkSection';
import { useMPCWidget } from '../../helpers/useMPCWidget';
import {
  cryptoData,
  cryptoSectionTranslations,
  getCryptoUrl,
} from '../../data/cryptoData';

interface Props {
  data: Queries.SeoPageQuery;
  locale: string;
  crypto: string;
  provider?: string;
  showLoginWidget?: boolean;
}

const PageContent: FC<Props> = ({
  data,
  locale,
  crypto,
  provider,
  showLoginWidget,
}) => {
  const { json } = data;
  const [hasScrolled, setHasScrolled] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const { hasAccount, isLoading, user } = useMPCWidget();

  const localeKey = (locale as 'en' | 'es' | 'ru' | 'zh') || 'en';
  const cryptoLinkPrefix =
    cryptoSectionTranslations.linkPrefix[localeKey] || 'Buy';

  const cryptoLinks = useMemo(
    () =>
      cryptoData
        .filter(c => c.slug !== crypto)
        .slice(0, 10)
        .map(c => ({
          slug: c.slug,
          label: `${cryptoLinkPrefix} ${c.names[localeKey] || c.names.en}`,
          url: getCryptoUrl(c.slug, locale),
        })),
    [locale, crypto, localeKey, cryptoLinkPrefix]
  );

  useEffect(() => {
    const handleScroll = () => {
      if (!hasScrolled) {
        setHasScrolled(true);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasScrolled]);

  useEffect(() => {
    const handleLoad = () => {
      setTimeout(() => {
        setShowVideo(true);
      }, 2000);
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }

    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  return (
    <>
      <Helmet>
      <html lang={locale} />
        <link
          rel="preconnect"
          href="https://fonts.bitcoin.com/Elza/Elza_Condensed_Black/crossmedia/Elza-CondensedBlack.woff"
          as="font"
          type="font/woff"
          crossOrigin="anonymous"
        />
        <link
          rel="preconnect"
          href="https://fonts.bitcoin.com/Satoshi_Variable/Fonts/WEB/fonts/Satoshi-Medium.woff"
          as="font"
          type="font/woff"
          crossOrigin="anonymous"
        />

        <style>
          {`
            @font-face {
              font-family: 'Elza Condensed';
              src: url('https://fonts.bitcoin.com/Elza/Elza_Condensed_Black/crossmedia/Elza-CondensedBlack.woff') format('woff');
              font-style: normal;
              font-display: swap;
            }
            @font-face {
              font-family: 'Satoshi Variable Medium';
              src: url('https://fonts.bitcoin.com/Satoshi_Variable/Fonts/WEB/fonts/Satoshi-Medium.woff') format('woff');
              font-weight: normal;
              font-style: normal;
              font-display: swap;
            }
            /* RTL Global Styles */
            html[dir="rtl"] {
              text-align: right;
            }
            html[dir="rtl"] * {
              text-align: right;
            }
            html[dir="rtl"] .ltr-text {
              direction: ltr;
              text-align: left;
            }
          `}
        </style>
        <script
          src={
            process.env.MPC_WIDGET_URL ||
            'https://widget.bitcoin.com/widget.v0.7.14.iife.js'
          }
          type="text/javascript"
        />
      </Helmet>
      <Navbar navbar={json?.navbar || null} locale={locale} />

      <main>
        <HeaderHome
          headerHome={json?.headerHome || null}
          crypto={crypto}
          provider={provider}
          showLoginWidget={showLoginWidget}
          isLoading={isLoading}
          hasAccount={hasAccount}
          user={user}
          showVideo={showVideo}
        />
        <WhyHome whyHome={json?.whyHome || null} />
        <GuideHome
          guideHome={json?.guideHome || null}
          showVideo={showVideo}
        />
        <BitcoinPriceSection
          bitcoinpricesection={json?.bitcoinpricesection || null}

        />
        <ReadytoGetStarted
          readytogetstarted={json?.readytogetstarted || null}
        />
        {json?.faq && <Faq faq={json?.faq || null} />}
      </main>

      <InterlinkSection
        title={
          cryptoSectionTranslations.cryptoPage.title[localeKey] ||
          'Other cryptocurrencies available to buy'
        }
        subtitle={
          cryptoSectionTranslations.cryptoPage.subtitle[localeKey] ||
          'Explore additional crypto assets ready to be purchased instantly at Bitcoin.com'
        }
        items={cryptoLinks}
      />

      <Footer footer={json?.footer || null} hasScrolled={hasScrolled} />
    </>
  );
};

export default PageContent;
